/**
 * D2C Collection Page JavaScript
 * Handles filtering, sorting, and compare functionality
 */

class D2CCollection {
  constructor() {
    this.compareProducts = this.loadCompareProducts();
    this.init();
  }

  init() {
    this.setupFilters();
    this.setupSort();
    this.setupCompare();
    this.updateCompareUI();
    this.restoreFiltersFromURL();
  }

  // ===== Filter Functionality =====

  setupFilters() {
    // Toggle filter groups
    const filterToggles = document.querySelectorAll(".d2c-filter-toggle");
    filterToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        toggle.classList.toggle("active");
      });
    });

    // Filter checkboxes
    const filterCheckboxes = document.querySelectorAll(
      '.d2c-filter-option input[type="checkbox"]'
    );
    filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.applyFilters();
        this.updateFilterCounts();
        this.updateURL();
      });
    });

    // Clear all filters
    const clearButton = document.getElementById("clearAllFilters");
    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.clearAllFilters();
      });
    }
  }

  applyFilters() {
    const activeFilters = this.getActiveFilters();
    const productCards = document.querySelectorAll(".d2c-product-card");
    let visibleCount = 0;

    productCards.forEach((card) => {
      const shouldShow = this.productMatchesFilters(card, activeFilters);

      if (shouldShow) {
        card.classList.remove("hidden");
        visibleCount++;
      } else {
        card.classList.add("hidden");
      }
    });

    // Update product count
    const countElement = document.getElementById("productCount");
    if (countElement) {
      countElement.textContent = visibleCount;
    }

    // Show/hide clear filters button
    const clearButton = document.getElementById("clearAllFilters");
    if (clearButton) {
      clearButton.style.display = this.hasActiveFilters() ? "block" : "none";
    }
  }

  getActiveFilters() {
    const filters = {
      product_type: [],
      use_on: [],
      attributes: [],
      cure_color: [],
    };

    document
      .querySelectorAll('.d2c-filter-option input[type="checkbox"]:checked')
      .forEach((checkbox) => {
        const filterType = checkbox.name;
        const filterValue = checkbox.value;
        if (filters[filterType]) {
          filters[filterType].push(filterValue);
        }
      });

    return filters;
  }

  productMatchesFilters(card, filters) {
    // If no filters are active, show all products
    if (!this.hasActiveFilters()) {
      return true;
    }

    const cardUseOn = card.dataset.useOn ? card.dataset.useOn.split(",") : [];
    const cardAttributes = card.dataset.attributes
      ? card.dataset.attributes.split(",")
      : [];
    const cardCureColor = card.dataset.cureColor || "";
    const cardProductType = card.dataset.productType || "";

    // Check product_type filter
    if (filters.product_type.length > 0) {
      const hasMatch = filters.product_type.includes(cardProductType);
      if (!hasMatch) return false;
    }

    // Check use_on filter
    if (filters.use_on.length > 0) {
      const hasMatch = filters.use_on.some((filter) =>
        cardUseOn.includes(filter)
      );
      if (!hasMatch) return false;
    }

    // Check attributes filter
    if (filters.attributes.length > 0) {
      const hasMatch = filters.attributes.some((filter) =>
        cardAttributes.includes(filter)
      );
      if (!hasMatch) return false;
    }

    // Check cure_color filter
    if (filters.cure_color.length > 0) {
      const hasMatch = filters.cure_color.includes(cardCureColor);
      if (!hasMatch) return false;
    }

    return true;
  }

  hasActiveFilters() {
    return (
      document.querySelectorAll(
        '.d2c-filter-option input[type="checkbox"]:checked'
      ).length > 0
    );
  }

  updateFilterCounts() {
    document.querySelectorAll(".d2c-filter-group").forEach((group) => {
      const toggle = group.querySelector(".d2c-filter-toggle");
      const countSpan = toggle.querySelector(".d2c-filter-count");
      const checkedCount = group.querySelectorAll(
        'input[type="checkbox"]:checked'
      ).length;

      if (checkedCount > 0) {
        countSpan.textContent = `(${checkedCount})`;
        countSpan.style.display = "inline";
      } else {
        countSpan.style.display = "none";
      }
    });
  }

  clearAllFilters() {
    document
      .querySelectorAll('.d2c-filter-option input[type="checkbox"]:checked')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });
    this.applyFilters();
    this.updateFilterCounts();
    this.updateURL();
  }

  restoreFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    ["product_type", "use_on", "attributes", "cure_color"].forEach((filterType) => {
      const values = urlParams.get(filterType);
      if (values) {
        values.split(",").forEach((value) => {
          const checkbox = document.querySelector(
            `input[name="${filterType}"][value="${value}"]`
          );
          if (checkbox) {
            checkbox.checked = true;
          }
        });
      }
    });

    if (this.hasActiveFilters()) {
      this.applyFilters();
      this.updateFilterCounts();
    }
  }

  updateURL() {
    const filters = this.getActiveFilters();
    const params = new URLSearchParams();

    Object.keys(filters).forEach((key) => {
      if (filters[key].length > 0) {
        params.set(key, filters[key].join(","));
      }
    });

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, "", newURL);
  }

  // ===== Sort Functionality =====

  setupSort() {
    const sortSelect = document.getElementById("sortBy");
    if (!sortSelect) return;

    sortSelect.addEventListener("change", (e) => {
      this.sortProducts(e.target.value);
    });
  }

  sortProducts(sortType) {
    const grid = document.getElementById("productGrid");
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(".d2c-product-card"));

    cards.sort((a, b) => {
      const cardA = this.getProductData(a);
      const cardB = this.getProductData(b);

      switch (sortType) {
        case "price-ascending":
          return (cardA.price || 0) - (cardB.price || 0);
        case "price-descending":
          return (cardB.price || 0) - (cardA.price || 0);
        case "title-ascending":
          return cardA.title.localeCompare(cardB.title);
        case "title-descending":
          return cardB.title.localeCompare(cardA.title);
        case "manual":
        default:
          return 0; // Keep original order
      }
    });

    // Re-append sorted cards
    cards.forEach((card) => grid.appendChild(card));
  }

  getProductData(card) {
    try {
      return JSON.parse(card.dataset.productData || "{}");
    } catch (e) {
      return {};
    }
  }

  // ===== Compare Functionality =====

  setupCompare() {
    // Compare checkboxes
    document.addEventListener("change", (e) => {
      if (e.target.classList.contains("compare-checkbox")) {
        this.handleCompareCheckbox(e.target);
      }
    });

    // Open compare modal button
    const openModalBtn = document.getElementById("openCompareModal");
    if (openModalBtn) {
      openModalBtn.addEventListener("click", () => {
        this.openCompareModal();
      });
    }

    // Close compare modal
    const closeModalBtn = document.getElementById("closeCompareModal");
    const modalOverlay = document.getElementById("compareModalOverlay");

    if (closeModalBtn) {
      closeModalBtn.addEventListener("click", () => {
        this.closeCompareModal();
      });
    }

    if (modalOverlay) {
      modalOverlay.addEventListener("click", () => {
        this.closeCompareModal();
      });
    }

    // ESC key to close modal
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeCompareModal();
      }
    });
  }

  handleCompareCheckbox(checkbox) {
    const productId = checkbox.dataset.productId;
    const card = checkbox.closest(".d2c-product-card");

    if (!card) return;

    if (checkbox.checked) {
      // Check if already at max
      if (this.compareProducts.length >= 4) {
        checkbox.checked = false;
        alert("You can only compare up to 4 products at a time.");
        return;
      }

      // Add to compare
      const productData = this.getProductData(card);
      if (productData.id) {
        this.compareProducts.push(productData);
      }
    } else {
      // Remove from compare
      this.compareProducts = this.compareProducts.filter(
        (p) => p.id != productId
      );
    }

    this.saveCompareProducts();
    this.updateCompareUI();
  }

  updateCompareUI() {
    const compareBar = document.getElementById("compareBar");
    const compareBarProducts = document.getElementById("compareBarProducts");
    const compareButton = document.getElementById("openCompareModal");

    if (!compareBar) return;

    // Show/hide compare bar
    if (this.compareProducts.length >= 2) {
      compareBar.classList.add("active");
      compareBar.style.display = "block";
    } else {
      compareBar.classList.remove("active");
      setTimeout(() => {
        if (!compareBar.classList.contains("active")) {
          compareBar.style.display = "none";
        }
      }, 300);
    }

    // Update products in bar
    if (compareBarProducts) {
      compareBarProducts.innerHTML = this.compareProducts
        .map(
          (product) => `
        <div class="d2c-compare-product-item">
          <div class="d2c-compare-product-thumb">
            <img src="${product.image}" alt="${this.escapeHtml(product.title)}">
          </div>
          <span class="d2c-compare-product-name">${this.escapeHtml(
            product.title
          )}</span>
          <button type="button" class="d2c-compare-product-remove" data-product-id="${
            product.id
          }" onclick="d2cCollection.removeFromCompare(${product.id})">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>
      `
        )
        .join("");
    }

    // Enable/disable compare button
    if (compareButton) {
      compareButton.disabled = this.compareProducts.length < 2;
    }

    // Update checkboxes state
    document.querySelectorAll(".compare-checkbox").forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      const isInCompare = this.compareProducts.some((p) => p.id == productId);
      checkbox.checked = isInCompare;
    });
  }

  removeFromCompare(productId) {
    this.compareProducts = this.compareProducts.filter(
      (p) => p.id != productId
    );
    this.saveCompareProducts();
    this.updateCompareUI();
  }

  openCompareModal() {
    if (this.compareProducts.length < 2) {
      return;
    }

    const modal = document.getElementById("compareModal");
    const compareTable = document.getElementById("compareTable");
    const compareTableHeader = document.getElementById("compareTableHeader");
    const compareTableBody = document.getElementById("compareTableBody");
    const compareEmpty = document.getElementById("compareEmpty");

    if (!modal) return;

    // Build table header
    let headerHTML = '<th class="d2c-compare-label-col"></th>';
    this.compareProducts.forEach((product) => {
      headerHTML += `
        <th class="d2c-compare-product-header">
          <div class="d2c-compare-product-image">
            <img src="${product.image}" alt="${this.escapeHtml(product.title)}">
          </div>
          <div class="d2c-compare-product-title"  style="text-align: left;">${this.escapeHtml(
            product.title
          )}</div>
          <a href="${
            product.url
          }" class="d2c-compare-product-link" style="text-align: left;">View Product</a>
        </th>
      `;
    });
    compareTableHeader.innerHTML = headerHTML;

    // Build table body
    const comparisonRows = [
      { label: "SURFACES", key: "useOn" },
      {
        label: "STRENGTH",
        key: "psi",
        format: (val) => (val ? `${val} PSI` : "—"),
      },
      { label: "SET TIME", key: "setTime" },
      { label: "CURE TIME", key: "cureTime" },
      { label: "CURE COLOR", key: "cureColor" },
    ];

    let bodyHTML = "";
    comparisonRows.forEach((row) => {
      bodyHTML += "<tr>";
      bodyHTML += `<td class="d2c-compare-label-col">${row.label}</td>`;

      this.compareProducts.forEach((product) => {
        let value = product[row.key] || "—";
        if (row.format && value !== "—") {
          value = row.format(value);
        }
        bodyHTML += `<td>${this.escapeHtml(value)}</td>`;
      });

      bodyHTML += "</tr>";
    });
    compareTableBody.innerHTML = bodyHTML;

    // Show modal
    if (compareTable) compareTable.style.display = "table";
    if (compareEmpty) compareEmpty.style.display = "none";
    modal.style.display = "flex";

    // Focus management for accessibility
    const closeButton = document.getElementById("closeCompareModal");
    if (closeButton) closeButton.focus();

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  closeCompareModal() {
    const modal = document.getElementById("compareModal");
    if (modal) {
      modal.style.display = "none";
      document.body.style.overflow = "";
    }
  }

  // ===== Storage =====

  loadCompareProducts() {
    try {
      const stored = localStorage.getItem("d2c-compare-products");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  saveCompareProducts() {
    try {
      localStorage.setItem(
        "d2c-compare-products",
        JSON.stringify(this.compareProducts)
      );
    } catch (e) {
      console.error("Failed to save compare products:", e);
    }
  }

  // ===== Utilities =====

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.d2cCollection = new D2CCollection();
  });
} else {
  window.d2cCollection = new D2CCollection();
}
